import { useState } from "react"
import { Navigate, useLocation, Link } from "react-router-dom"
import { motion } from "motion/react"
import { ArrowRight, KeyRound, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/hooks/use-auth"
import { useServerInfo } from "@/hooks/use-server-info"
import { useSession, type SessionUser } from "@/hooks/use-session"
import { AuthLayout } from "@/components/auth-layout"
import { AuthCard, AuthHeading, LogoMark } from "@/components/auth-card"
import { AuthField, AuthErrorBanner } from "@/components/auth-field"

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { info, loading } = useServerInfo()

  if (loading) {
    return <BootSplash />
  }

  if (info?.edition === "saas") {
    return <SaasGate>{children}</SaasGate>
  }

  return <OssGate>{children}</OssGate>
}

function SaasGate({ children }: { children: React.ReactNode }) {
  const session = useSession()
  const location = useLocation()

  if (session.status === "loading") {
    return <BootSplash />
  }
  if (session.status === "unauthenticated") {
    return <Navigate to="/login" replace />
  }
  // First-run gate: a new workspace must finish onboarding before the app opens.
  if (!session.user.onboarding_complete) {
    return <Navigate to="/onboarding" replace />
  }
  // Expired trial / lapsed plan: wall the app behind upgrade, but keep the
  // upgrade page itself reachable so they can recover.
  if (isExpired(session.user) && location.pathname !== "/upgrade") {
    return <TrialEndedWall />
  }
  return <>{children}</>
}

function isExpired(u: SessionUser): boolean {
  if (u.billing_status === "expired") return true
  if (u.billing_status === "trialing" && u.trial_ends_at) {
    return new Date(u.trial_ends_at).getTime() < Date.now()
  }
  return false
}

function TrialEndedWall() {
  const logout = async () => {
    await fetch("/api/v1/saas/auth/logout", { method: "POST", credentials: "include" }).catch(() => {})
    window.location.href = "/login"
  }
  return (
    <AuthLayout>
      <LogoMark />
      <AuthCard>
        <div className="space-y-6 text-center">
          <AuthHeading
            eyebrow="Trial ended"
            title="Your free trial has ended"
            description="Pick a plan to keep receiving webhooks and using your workspace. Your data is safe and resumes the moment you upgrade."
          />
          <Button asChild className="group w-full bg-rose-600 text-white hover:bg-rose-500">
            <Link to="/upgrade">
              Upgrade to continue
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Button>
          <button
            type="button"
            onClick={logout}
            className="w-full text-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Log out
          </button>
        </div>
      </AuthCard>
    </AuthLayout>
  )
}

function OssGate({ children }: { children: React.ReactNode }) {
  const { isConfigured } = useAuth()
  if (isConfigured) {
    return <>{children}</>
  }
  return <ApiKeyGate />
}

function BootSplash() {
  return (
    <AuthLayout>
      <div className="flex items-center justify-center py-10 text-muted-foreground/60">
        <motion.div
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="text-2xs uppercase tracking-[0.22em]"
        >
          Loading
        </motion.div>
      </div>
    </AuthLayout>
  )
}

function ApiKeyGate() {
  const { setApiKey } = useAuth()
  const [key, setKey] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [validating, setValidating] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = key.trim()
    if (!trimmed) return

    setError(null)
    setValidating(true)
    try {
      const res = await fetch("/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${trimmed}`,
        },
        credentials: "include",
        body: JSON.stringify({ query: "{ status { version } }" }),
      })

      if (!res.ok) {
        setError(
          res.status === 401
            ? "That API key isn't valid for this server."
            : `Server returned ${res.status}. Try again.`,
        )
        return
      }

      const body = (await res.json()) as { errors?: { message: string }[] }
      const firstError = body.errors?.[0]
      if (firstError) {
        setError(firstError.message)
        return
      }
      setApiKey(trimmed)
    } catch {
      setError("Couldn't reach the server. Is it running?")
    } finally {
      setValidating(false)
    }
  }

  return (
    <AuthLayout>
      <LogoMark />
      <AuthCard>
        <div className="space-y-7">
          <AuthHeading
            eyebrow="Enterprise"
            title="Authenticate with your API key"
            description="Paste the bearer token configured on your server. It stays in this browser."
          />

          <form onSubmit={handleSubmit} className="space-y-4">
            <AuthField label="API key">
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/50" />
                <Input
                  type="password"
                  placeholder="hvk_..."
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  className="pl-9 font-mono text-sm"
                  autoFocus
                  spellCheck={false}
                />
              </div>
            </AuthField>

            {error && <AuthErrorBanner message={error} />}

            <Button
              type="submit"
              disabled={validating}
              className="group w-full bg-rose-600 text-white shadow-lg shadow-rose-500/20 transition-all hover:bg-rose-500 hover:shadow-rose-500/30 disabled:bg-rose-600/60"
            >
              {validating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying
                </>
              ) : (
                <>
                  Authenticate
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>

          <div className="border-t border-border/40 pt-4 text-center text-2xs uppercase tracking-[0.18em] text-muted-foreground/50">
            <a
              href="https://hivehook.com/docs#api-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground/70"
            >
              Where do I find this?
            </a>
          </div>
        </div>
      </AuthCard>
    </AuthLayout>
  )
}

export default AuthGate
